import React from 'react';

import UserService from "../../services/user";
import PackageService from "../../services/package";
import PostService from "../../services/post";
import PreferenceService from "../../services/preference";

import AvatarImage from "../../components/avatar-image";
import CoverImage from "../../components/cover-image";
import LoadingIndicator from "../../components/loading-indicator";
import Navigation from '../../components/navigation';
import Footer from '../../components/footer';
import ResponsiveNavigation from "../../components/responsive-navigation";
import HelmetComponent from "../../components/helmet";
import PackageList from "../../components/package-list";
import ContentConstants from "../../constants/content";
import PostList from "../../components/post-list";
import SubscriptionConfirmPopup from "../../components/subscription-confirm-popup";

class UserPage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            userData: '',
            userPackages: [],
            packagePaging: '',
            userPosts: [],
            postsPaging: '',
            isLoadingPosts: true,
            isLoadingFollowing: !!props.currentUser,
            isFollowing: false,
            isLoadingMore: false,
            isLoading: true,
            selectedContent: ContentConstants.POSTS,
            isShowingSubscriptionConfirm: false,
            currentUserCard: null,
            showFollowToChatPopup: false,
            showUnfollowConfirm: false,
            shareLabel: null,
        };

        this.fetchPackages = this.fetchPackages.bind(this);
        this.fetchPosts = this.fetchPosts.bind(this);
        this.handleUserPackageFetchSuccess = this.handleUserPackageFetchSuccess.bind(this);
        this.follow = this.follow.bind(this);
        this.unfollow = this.unfollow.bind(this);
        this.confirmUnfollow = this.confirmUnfollow.bind(this);
        this.handleShare = this.handleShare.bind(this);
        this.loadMore = this.loadMore.bind(this);
        this.attemptLoadMore = this.attemptLoadMore.bind(this);
        this.handleContentSwitch = this.handleContentSwitch.bind(this);
    }

    componentDidMount() {
        PreferenceService.subscribe('explicitFilterStateChange', () => this.fetchPackages(this.state.userData));
        this.handlePostCreated = (createdPost) => {
            if (!this.isVisitedUserSelf()) return;
            if (createdPost && createdPost._id) {
                this.setState(prev => ({
                    userPosts: [createdPost, ...(prev.userPosts || []).filter(p => p._id !== createdPost._id)],
                    isLoadingPosts: false,
                }));
                return;
            }
            this.setState({ userPosts: [], postsPaging: '' });
            this.fetchPosts(this.state.userData);
        };
        PreferenceService.subscribe('postCreated', this.handlePostCreated);

        this.fetchUser();
        if (this.props.currentUser) {
            UserService.fetchCurrent().then(res => {
                const data = res.data;
                if (data.card_last4) {
                    this.setState({ currentUserCard: { last4: data.card_last4, brand: data.card_brand, paymentMethodId: data.stripe_payment_method_id } });
                }
            }).catch(() => {});
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.location.pathname !== this.props.location.pathname) {
            this.setState({
                userData: '',
                userPackages: [],
                userPosts: [],
                packagePaging: '',
                postsPaging: '',
                isLoading: true,
                isLoadingPosts: true,
                isFollowing: false,
                isLoadingFollowing: !!this.props.currentUser,
            });
            this.fetchUser();
        }
    }

    componentWillUnmount() {
        PreferenceService.unsubscribe('explicitFilterStateChange', () => this.fetchPackages(this.state.userData));
        PreferenceService.unsubscribe('postCreated', this.handlePostCreated);
    }

    getUserId() {
        return this.state.userData._id ? this.state.userData._id : null;
    }

    getAvatarKey() {
        return this.getUserId() ? this.getUserId() : Math.random();
    }

    getUserPublicName() {
        return (this.state.userData && this.state.userData.publicName) ? this.state.userData.publicName : '';
    }

    fetchUser(fetchUserContent = true) {
        const visitedUser = this.retrievePublicName();

        if (visitedUser) {
            UserService.fetch(visitedUser).then(response => this.handleUserFetchSuccess(response.data, fetchUserContent)).catch(error => this.props.navigation.featured());
        } else {
            UserService.fetchCurrent().then(response => this.handleUserFetchSuccess(response.data, fetchUserContent)).catch(error => console.log(error));
        }
    }

    loadMore() {
        this.setState({isLoadingMore: true});
        this.state.selectedContent === ContentConstants.PACKAGES ? this.fetchPackages(this.state.userData, this.state.packagePaging ? this.state.packagePaging.nextKey : null) : this.fetchPosts(this.state.userData, this.state.postsPaging ? this.state.postsPaging.nextKey : null);
    }

    attemptLoadMore() {
        if (this.canLoadMore()) {
            this.loadMore();
        }
    }

    isLoggedIn() {
        return !!this.props.currentUser;
    }

    fetchIsFollowing() {
        UserService.isFollowing(this.getUserId())
            .then(response => this.handleIsFollowingSuccess(response))
            .catch(() => this.setState({ isFollowing: false, isLoadingFollowing: false }));
    }

    handleIsFollowingSuccess(response) {
        if (!response || !response.data || !response.data.following || response.data.following.length === 0) {
            this.setState({ isFollowing: false, isLoadingFollowing: false });
        } else {
            this.setState({ isFollowing: true, isLoadingFollowing: false });
        }
    }

    retrievePublicName() {
        const segment = this.props.location.pathname.split("/")[1] ?? '';
        return segment.startsWith('@') ? segment.slice(1) : null;
    }

    fetchPackages(userData, nextKey) {
        let newUserData = userData ? userData : this.state.userData;
        PackageService.fetchPackagesForUser(newUserData._id, nextKey, 25).then(response => this.handleUserPackageFetchSuccess(response.data.packages, response.data.paging)).catch(error => this.handleUserPackageFetchFailure(error));
    }

    fetchPosts(userData, nextKey) {
        let newUserData = userData ? userData : this.state.userData;
        this.setState({ isLoadingPosts: true });
        PostService.fetchPostsForUserName(newUserData.publicName, nextKey, 25).then(response => this.handleUserPostsFetchSuccess(response.data.posts, response.data.paging)).catch(error => this.handleUserPostsFetchFailure(error));
    }

    follow(event) {
        event.preventDefault();

        if (!this.isAuthenticated()) {
            this.props.navigation.signIn(this.props.location.pathname);
            return;
        }

        const price = this.state.userData?.subscription_price;
        if (price != null && price > 0) {
            this.setState({ isShowingSubscriptionConfirm: true });
            return;
        }

        this.doFollow();
    }

    doFollow() {
        this.setState({ isLoadingFollowing: true, isShowingSubscriptionConfirm: false });
        UserService.follow(this.state.userData._id).then(() => {
            this.fetchUser(false);
        });
    }

    unfollow(event) {
        event.preventDefault();
        this.setState({ showUnfollowConfirm: true });
    }

    confirmUnfollow() {
        this.setState({ showUnfollowConfirm: false, isLoadingFollowing: true });
        UserService.unfollow(this.state.userData._id).then(() => {
            this.fetchUser(false);
        });
    }

    handleUserFetchSuccess(userData, fetchUserContent) {
        this.setState({ userData: userData });

        if (fetchUserContent) {
            this.fetchPackages(userData, null);
            this.fetchPosts(userData, null);
        }

        if (this.isLoggedIn()) {
            this.fetchIsFollowing(userData._id);
        } else {
            this.setState({ isLoadingFollowing: false });
        }
    }

    handleUserPackageFetchSuccess(userPackages, paging) {
        this.setState((prevState) => ({ userPackages: prevState.userPackages.concat(userPackages), packagePaging: paging, isLoading: false, isLoadingMore: false }));
    }

    handleUserPostsFetchSuccess(userPosts, paging) {
        const pending = this.isVisitedUserSelf() ? PreferenceService.consumePendingCreatedPost() : null;
        this.setState((prevState) => {
            const combined = prevState.userPosts.concat(userPosts);
            if (pending && pending._id && !combined.some(p => p._id === pending._id)) {
                return { userPosts: [pending, ...combined], postsPaging: paging, isLoading: false, isLoadingMore: false, isLoadingPosts: false };
            }
            return { userPosts: combined, postsPaging: paging, isLoading: false, isLoadingMore: false, isLoadingPosts: false };
        });
    }

    handleUserPackageFetchFailure(error) {
        if (error) {
            this.setState({userPackages: [], packagePaging: '', isLoading: false, isLoadingMore: false});
        }
    }

    handleUserPostsFetchFailure(error) {
        if (error) {
            this.setState({userPosts: [], postsPaging: '', isLoading: false, isLoadingMore: false, isLoadingPosts: false});
        }
    }

    userHasPackages() {
        return Array.isArray(this.state.userPackages) && this.state.userPackages.length > 0;
    }

    userHasPosts() {
        return Array.isArray(this.state.userPosts) && this.state.userPosts.length > 0;
    }

    isLoading() {
        return this.state.isLoading;
    }

    canLoadMore() {
        return this.state.selectedContent === ContentConstants.PACKAGES ? !!this.state.packagePaging.nextKey : !!this.state.postsPaging.nextKey;
    }

    isAuthenticated() {
        return !!this.props.currentUser;
    }

    isVisitedUserSelf() {
        return this.props.currentUser && this.state.userData && (this.state.userData._id === this.props.currentUser.userId);
    }

    shouldDisplayFollowButton() {
        return !this.state.isFollowing && !this.isVisitedUserSelf();
    }

    shouldDisplayUnfollowButton() {
        return this.state.isFollowing && !this.isVisitedUserSelf();
    }

    handleContentSwitch(selectedContentEvent) {
        const selectedContent = selectedContentEvent.target.id === 'posts' ? ContentConstants.POSTS : ContentConstants.PACKAGES;
        this.setState({selectedContent});
    }

    renderNavigation() {
        return <Navigation isAuthenticated={this.isAuthenticated()} currentUser={this.props.currentUser} navigation={this.props.navigation}/>
    }

    renderFooter() {
        return <Footer />
    }

    renderRenderResponsiveNavigation() {
        return <ResponsiveNavigation isAuthenticated={this.isAuthenticated()} currentUser={this.props.currentUser} navigation={this.props.navigation}/>
    }

    renderFollowButton(idiom) {
        const price = this.state.userData?.subscription_price;
        const priceLabel = price != null && price > 0 ? ` · $${price}/mo` : '';
        return <div className={idiom === 'mobile' ? "follow-button-mobile" : "follow-button-desktop"}>
                    <a href={`/follow`} onClick={this.follow}>Follow{priceLabel}</a>
        </div>;
    }

    async handleShare(event) {
        event.preventDefault();
        const name = this.getUserPublicName();
        if (!name) return;
        const url = `${window.location.origin}/@${name}`;
        const shareData = { title: `@${name} on OpenSwap`, text: `Check out @${name} on OpenSwap`, url };

        if (navigator.share) {
            try { await navigator.share(shareData); return; }
            catch (e) { if (e?.name === 'AbortError') return; }
        }

        try {
            await navigator.clipboard.writeText(url);
            this.setState({ shareLabel: 'Link copied!' });
        } catch (e) {
            this.setState({ shareLabel: 'Copy failed' });
        }
        setTimeout(() => this.setState({ shareLabel: null }), 2000);
    }

    renderShareButton(idiom) {
        const label = this.state.shareLabel || 'Share';
        return <div className={idiom === 'mobile' ? "follow-button-mobile" : "follow-button-desktop"}>
            <a href="#share" onClick={this.handleShare} aria-label="Share profile">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: 6 }}>
                    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                {label}
            </a>
        </div>;
    }

    renderChatButton(idiom) {
        const canChat = !this.isAuthenticated() || this.state.isFollowing;
        const handleChatClick = (e) => {
            e.preventDefault();
            if (!this.isAuthenticated()) return this.props.navigation.signIn();
            if (!this.state.isFollowing) return this.setState({ showFollowToChatPopup: true });
            this.props.navigation.chat(this.state.userData.publicName);
        };
        return <div className={idiom === 'mobile' ? "follow-button-mobile" : "follow-button-desktop"}>
                    <a href={`/chat/${this.state.userData.publicName}`}
                       onClick={handleChatClick}
                       className={!canChat ? 'chat-button-disabled' : ''}>Chat</a>
        </div>;
    }

    renderFollowToChatPopup() {
        const name = this.getUserPublicName();
        const price = this.state.userData?.subscription_price;
        const followLabel = price > 0 ? `Follow · $${price}/mo` : 'Follow';
        return <div className="share-popup-backdrop" onClick={() => this.setState({ showFollowToChatPopup: false })}>
            <div className="share-popup follow-to-chat-popup" onClick={e => e.stopPropagation()}>
                <div className="share-popup-header">
                    <span className="share-popup-title">Start a chat</span>
                    <button className="share-popup-close" onClick={() => this.setState({ showFollowToChatPopup: false })}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
                <div className="follow-to-chat-popup-icon">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#0f3c4b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                </div>
                <p className="follow-to-chat-popup-body">Follow <strong>@{name}</strong> to send them a message.</p>
                <button className="follow-to-chat-popup-cta" onClick={() => { this.setState({ showFollowToChatPopup: false }); this.follow({ preventDefault: () => {} }); }}>{followLabel}</button>
                <button className="follow-to-chat-popup-dismiss" onClick={() => this.setState({ showFollowToChatPopup: false })}>Maybe later</button>
            </div>
        </div>;
    }

    renderUnfollowConfirmPopup() {
        const name = this.getUserPublicName();
        const price = this.state.userData?.subscription_price;
        const hint = price > 0
            ? `You'll stop seeing @${name}'s posts in your feed. Your subscription stays active and you keep access to their content until it ends — cancel the subscription separately if you want billing to stop.`
            : `You'll stop seeing @${name}'s posts in your feed.`;
        return <div className="share-popup-backdrop" onClick={() => this.setState({ showUnfollowConfirm: false })}>
            <div className="share-popup follow-to-chat-popup" onClick={e => e.stopPropagation()}>
                <div className="share-popup-header">
                    <span className="share-popup-title">Unfollow @{name}?</span>
                    <button className="share-popup-close" onClick={() => this.setState({ showUnfollowConfirm: false })}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
                <p className="follow-to-chat-popup-body">{hint}</p>
                <button className="follow-to-chat-popup-cta" onClick={this.confirmUnfollow}>Unfollow</button>
                <button className="follow-to-chat-popup-dismiss" onClick={() => this.setState({ showUnfollowConfirm: false })}>Cancel</button>
            </div>
        </div>;
    }

    renderUnfollowButton(idiom) {
        return <div className={idiom === 'mobile' ? "follow-button-mobile" : "follow-button-desktop"}>
                    <a href={`/unfollow`} onClick={this.unfollow}>Unfollow</a>
        </div>;
    }

    renderFollowLoading(idiom) {
        if (idiom === 'mobile') {
            return <div className="follow-button-mobile">
                <div className="follow-button-loading-shell">
                    <div className="follow-button-spinner"></div>
                </div>
            </div>;
        }
        return <div className="follow-button-desktop">
            <LoadingIndicator positionClassName='follow-loading-indicator-position' contentClassName='follow-loading-indicator' customPosition={true}/>
        </div>;
    }

    isPostsLockedBySubscription() {
        const price = this.state.userData?.subscription_price;
        return price > 0 && !this.isVisitedUserSelf() && !this.state.isFollowing && !this.state.isLoadingFollowing;
    }

    renderLockedPosts() {
        const price = this.state.userData?.subscription_price;
        const name = this.getUserPublicName();
        return <div className="posts-subscription-gate">
            <span className="posts-subscription-gate-icon">🔒</span>
            <p className="posts-subscription-gate-label">Subscribe to see {name}'s posts</p>
            <p className="posts-subscription-gate-price">${price}/month</p>
        </div>;
    }

    renderPosts() {
        if (this.isPostsLockedBySubscription()) {
            return this.renderLockedPosts();
        }
        if (this.state.isLoadingPosts) {
            return <LoadingIndicator />;
        }
        return <div className={!this.userHasPosts() ? "row block-display" : "row"}>
            <PostList posts={this.state.userPosts} navigation={this.props.navigation} loadMore={this.attemptLoadMore} isAuthenticated={this.isAuthenticated()} currentUser={this.props.currentUser} />
        </div>;
    }

    renderPackages() {
        return <div className={!this.userHasPackages() ? "row block-display" : "row"}>
            <PackageList packages={this.state.userPackages} navigation={this.props.navigation} loadMore={this.attemptLoadMore} isAuthenticated={this.isAuthenticated()} />
        </div>;
    }

    renderContent() {
        return <section key={this.getUserId()} className="featured my">
            <CoverImage identityId={this.getUserId()}>
                { this.state.isLoadingFollowing ? this.renderFollowLoading('mobile') : '' }
                { !this.state.isLoadingFollowing && this.shouldDisplayFollowButton() && this.renderFollowButton('mobile') }
                { !this.state.isLoadingFollowing && this.shouldDisplayUnfollowButton() && this.renderUnfollowButton('mobile') }
                { !this.isVisitedUserSelf() && this.renderChatButton('mobile') }
                { this.renderShareButton('mobile') }
            </CoverImage>

            <div className="container bio">
                <div className="row">
                    <div className="col-md-auto user-header-col-padding">
                        <div className="user-header">
                            <AvatarImage identityId={this.getUserId()} />
                            <div className='user-profile-display-name'>{this.state.userData.name}</div>
                            <div className='user-profile-username'>@{this.state.userData.publicName}</div>
                        </div>
                        <div className="mobile-user-header">
                            <AvatarImage identityId={this.getUserId()} extraClassNames="user-img" />
                            <div className="user-header-name">
                                <div className="mobile-user-header-display-name">{this.state.userData.name}</div>
                                <div className="mobile-user-header-username">@{this.state.userData.publicName}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-9 user-bio-padding desktop-hide">
                        <span>{this.state.userData.bio === '' ? "This user prefers to maintain an air of mystery..." : this.state.userData.bio}</span>
                    </div>
                </div>
                <div className='row desktop-user-control-panel'>
                    { this.state.isLoadingFollowing ? this.renderFollowLoading('desktop') : '' }
                    { !this.state.isLoadingFollowing && this.shouldDisplayFollowButton() && this.renderFollowButton('desktop') }
                    { !this.state.isLoadingFollowing && this.shouldDisplayUnfollowButton() && this.renderUnfollowButton('desktop') }
                    { !this.isVisitedUserSelf() && this.renderChatButton('desktop') }
                    { this.renderShareButton('desktop') }
                </div>
            </div>
            <div className="container mobile-hide">
                <div className='row desktop-user-description'>
                    <span>{this.state.userData.bio === '' ? "This user prefers to maintain an air of mystery..." : this.state.userData.bio}</span>
                </div>
            </div>
            <div className='user-page-content-selector'>
                <h3 onClick={this.handleContentSwitch} id='posts' className={this.state.selectedContent === ContentConstants.POSTS ? 'user-page-content-selector-posts' : 'user-page-content-selector-posts user-page-content-selector-unselected'}>Posts</h3>
                <h3 onClick={this.handleContentSwitch} id='packages' className={this.state.selectedContent === ContentConstants.PACKAGES ? 'user-page-content-selector-packages' : 'user-page-content-selector-packages user-page-content-selector-unselected'}>Packages</h3>
            </div>
            <div className={this.state.selectedContent === ContentConstants.POSTS ? 'container posts' : 'container'} id="container">
                {this.state.selectedContent === ContentConstants.POSTS ? this.renderPosts() : this.renderPackages()}
            </div>
        </section>;
    }

    renderLoading() {
        return <LoadingIndicator/>;
    }

    renderUserPage() {
        return <div>
            <header>
                <div className="nav-container">
                    <div className="container">
                        {this.renderNavigation()}
                    </div>
                </div>
            </header>

            {this.isLoading() ? this.renderLoading() : this.renderContent()}

            {this.renderFooter()}
            {this.renderRenderResponsiveNavigation()}
            <HelmetComponent title={`Creator - ${this.getUserPublicName()}`} description={`OpenSwap.io creator page for ${this.getUserPublicName()}.`} />
            {this.state.showFollowToChatPopup && this.renderFollowToChatPopup()}
            {this.state.showUnfollowConfirm && this.renderUnfollowConfirmPopup()}
            {this.state.isShowingSubscriptionConfirm && (
                <SubscriptionConfirmPopup
                    publicName={this.state.userData.publicName}
                    creatorId={this.state.userData._id}
                    pricePerMonth={this.state.userData.subscription_price}
                    savedCard={this.state.currentUserCard}
                    onSuccess={() => {
                        this.setState({
                            isShowingSubscriptionConfirm: false,
                            isFollowing: true,
                            isLoadingFollowing: false,
                            userPosts: [],
                            postsPaging: '',
                        });
                        this.fetchPosts(this.state.userData);
                    }}
                    onCancel={() => this.setState({ isShowingSubscriptionConfirm: false })}
                    onAddPaymentMethod={() => {
                        this.setState({ isShowingSubscriptionConfirm: false });
                        this.props.navigation.settings('financial');
                    }}
                />
            )}
        </div>

    }

    render() {
        return this.renderUserPage();
    }
}

export default UserPage;
